@import '../variables.module.less';

@import '@/styles/mixins/index.less';

@page-header-baseline-top: 32px;
@page-header-control-size: 48px;
@page-header-bottom-spacing: 8px;

.header,
.headerSpacer {
  --page-header-top-spacing: max(var(--safe-area-inset-top), @page-header-baseline-top);
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-right: 20px;
  padding-left: 20px;
  width: 100%;
  // ponytail: root app container already caps content at max-width 700px and
  // centres it. `position: fixed` escapes that container, so without this cap
  // the header bar spans the full viewport on desktop and the back/action
  // buttons drift away from the 700px content column. Auto margins re-centre
  // the capped box between left:0/right:0. No mobile change (< 700px).
  max-width: 700px;
  margin-left: auto;
  margin-right: auto;

  // Fixed to the top of the viewport. `position: sticky` breaks here because
  // the auth layout has an intermediate `overflow-y: auto` wrapper that
  // traps the sticky containing block. `fixed` bypasses it and stays on
  // top on scroll.
  // !important beats page-level `.page > * { position: relative; z-index: 1 }`
  // rules that would otherwise demote the header back to relative.
  position: fixed !important;
  top: 0 !important;
  left: 0 !important;
  right: 0 !important;
  z-index: var(--z-sticky) !important;

  // The inset replaces (rather than stacks on) the normal 32px page baseline.
  // globals.less supplies browser/PWA env() values and Telegram may override
  // the same variable on either platform.
  align-items: center;
  padding-top: var(--page-header-top-spacing);
  padding-bottom: @page-header-bottom-spacing;

  // Default (scroll = 0): fully transparent — no jarring white bar. Buttons
  // float on the page content.
  background: transparent;

  // Glass lives on a ::before layer so its bottom edge can dissolve (masked)
  // independently of the header's buttons. Transparent at rest; fades in via
  // [data-scrolled] — mirrors the discovery sticky bar. A CSS transition (not
  // per-frame scroll tracking) drives the fade, so it's smooth in Safari/iOS.
  &::before {
    .sticky-glass-before(16px);
  }

  &[data-scrolled='true']::before {
    .sticky-glass-on(rgba(var(--color-surface-rgb, 255, 255, 255), 0.6); blur(10px) saturate(180%));
  }
}

.left,
.right {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 48px;
}

.center {
  flex: 1;
  display: flex;
  justify-content: center;
}

// Every host page already contributes the 32px design baseline. Offset only
// the remainder of the fixed header so baseline + spacer equals its rendered
// height on both zero-inset Android and inset-bearing iOS/Telegram viewports.
.headerSpacer {
  height: calc(
    @page-header-control-size + @page-header-bottom-spacing + var(--page-header-top-spacing) -
      @page-header-baseline-top
  );
}

// Back button styles
.backButton {
  width: @page-header-control-size;
  height: @page-header-control-size;
  border-radius: 38px;
  border: 1px solid @v2-color-border;
  background: @v2-color-bg-white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background-color @v2-duration-fast ease;

  .hover-supported({
      background: @v2-color-hover;
  

  });

  &:active {
    transform: scale(0.98);
  }
}

.backButtonWhite {
  // Photo-overlay chrome: stays literal white over imagery in both themes.
  background: white;
  border-color: white;
}

// Action button (icon button) styles
.actionButton {
  position: relative;
  width: @page-header-control-size;
  height: @page-header-control-size;
  border-radius: 38px;
  border: 1px solid @v2-color-border;
  background: @v2-color-secondary;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: @v2-color-text-light;
  transition: background-color @v2-duration-fast ease;

  .hover-supported({
      background: @v2-color-hover-dark;
  

  });

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &.actionButtonReacting {
    opacity: 1;
  }

  &:active:not(:disabled) {
    transform: scale(0.98);
  }
}

.actionButtonPrimary {
  background: @v2-color-primary;

  .hover-supported({
      background: @v2-color-active;
  

  });
}

.actionButtonActive {
  background: @v2-color-like;

  .hover-supported({
    background: color-mix(in srgb, @v2-color-like 85%, black);
  });
}

.actionButtonDislike {
  background: @v2-color-accent !important;

  .hover-supported({
    background: color-mix(in srgb, @v2-color-accent 90%, black) !important;
  });
}

// Share button styles
.shareButton {
  background-color: @v2-color-primary;
  color: @v2-color-text-light;
  border: none;
  border-radius: 25px;
  padding: 12px 24px;
  font-size: 12px;
  font-weight: 900;
  cursor: pointer;
  transition: background-color @v2-duration-fast ease;

  .hover-supported({
      background: @v2-color-active;
  

  });

  &:active {
    transform: scale(0.98);
  }
}
